import { useMutation, useQuery } from "@tanstack/react-query";

import {
  agendaResponseSchema,
  eventsResponseSchema,
  protocolResponseSchema,
  summariesResponseSchema,
  ticketSummaryDtoSchema,
  type AgendaResponse,
  type ProtocolRequest,
  type SummariesRequest,
  type UpcomingEvent,
} from "@msc/shared";

import { api } from "../lib/api.js";

// Die Shapes wohnen als Zod-Schema in `packages/shared` und werden hier wie im
// Backend aus derselben Quelle benutzt (s. `useBcg.ts`). Der `parse` beim
// Lesen fängt eine auseinanderlaufende Antwort dort, wo sie entsteht, statt
// später als `undefined` auf der Bühne.

const enc = encodeURIComponent;

/**
 * Je eine Folien-Zusammenfassung für das übergebene Deck. `missingKeys` nennt
 * Schlüssel ohne Ticket beim Kunden — die Anfrage scheitert daran nicht.
 * Kein globaler Erfolgs-Toast (`meta.silent`): Der Aufrufer zeigt selbst an,
 * wann das Deck fertig ist.
 */
export function useGenerateSummaries() {
  return useMutation({
    mutationFn: async (body: SummariesRequest) =>
      summariesResponseSchema.parse(await api.post("/customer-view/presentation/summaries", body)),
    meta: { silent: true },
  });
}

/** Redaktion einer Zusammenfassung durch den Lead. */
export function useEditSummary() {
  return useMutation({
    mutationFn: async ({ key, ...body }: { key: string; text: string; issueUpdatedAt: string }) =>
      ticketSummaryDtoSchema.parse(await api.put(`/customer-view/presentation/summaries/${enc(key)}`, body)),
    meta: { silent: true },
  });
}

/** Ergebnisprotokoll als Markdown; das Fazit ist fail-soft (kann fehlen). */
export function useCreateProtocol() {
  return useMutation({
    mutationFn: async (body: ProtocolRequest) =>
      protocolResponseSchema.parse(await api.post("/customer-view/presentation/protocol", body)),
    meta: { silent: true },
  });
}

/**
 * Die drei Agenda-Punkte eines Termins (D9) — Vorlage des Bereichs, mit
 * CRM-Daten vorbefüllt. `linked: false` heißt: Die Punkte kommen ohne
 * Wartungs- und Budgettabelle, `reason` sagt warum (die Vorbereitung zeigt
 * dann einen Hinweis).
 *
 * Der Kundenname reist als Abfrageparameter mit, weil der Platzhalter
 * `{{kunde}}` auf dem Server aufgelöst wird und der Client den Namen längst
 * aus der Kundenliste hat.
 *
 * `staleTime` 5 Minuten: Wartungsstände und Budgets ändern sich nicht im
 * Verlauf eines Termins, und die Vorbereitung soll beim Hin- und Herschalten
 * nicht bei jedem Rendern neu laden.
 */
export function useAgenda(objectKey: string | null, name: string) {
  return useQuery<AgendaResponse>({
    // Der Name geht in die Abfrage ein (Jira-Suche nach Neuigkeiten) — ohne
    // ihn im Schlüssel bekäme ein Kunde, dessen Name sich zwischen zwei
    // Aufrufen ändert, still die Antwort zum alten Namen aus dem Cache.
    queryKey: ["customer-view", "agenda", objectKey, name],
    queryFn: async () =>
      agendaResponseSchema.parse(
        await api.get(`/customer-view/customers/${enc(objectKey ?? "")}/agenda?name=${enc(name)}`),
      ),
    // Ohne Namen antwortet die Route mit 400 (`name` ist Pflicht) — gar nicht
    // erst fragen ist ehrlicher als ein roter Fehler in der Vorbereitung.
    enabled: !!objectKey && name.length > 0,
    staleTime: 5 * 60 * 1000,
  });
}

/** Termine der öffentlichen Bereichs-Eventseite für die Endfolie. */
export function useEvents(bereich: string | null, enabled: boolean) {
  return useQuery<{ events: UpcomingEvent[] }>({
    queryKey: ["customer-view", "events", bereich],
    queryFn: async () =>
      eventsResponseSchema.parse(
        await api.get(`/customer-view/events?bereich=${enc(bereich ?? "")}`),
      ),
    enabled,
    staleTime: 6 * 60 * 60 * 1000,
    retry: false,
  });
}
